/**
 * Where an app's API is. `VITE_API_URL` names it; empty means "the origin
 * the page came from", which is what a dev server proxying `/v1` wants.
 *
 * Read lazily so importing the package never throws over an unset variable
 * the caller may not need.
 */
function configuredApiUrl(): string {
    return (import.meta.env?.VITE_API_URL as string | undefined) ?? "";
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, "");
}

function isLoopbackHostname(hostname: string): boolean {
    const normalized = hostname.toLowerCase();
    return normalized === "localhost" || normalized === "127.0.0.1";
}

/**
 * A loopback API named from a page that is not itself on loopback cannot be
 * reached by the browser, so the request goes to the page's own origin and
 * whatever proxies there.
 */
function shouldUseProxy(rawBaseUrl: string): boolean {
    if (typeof window === "undefined") {
        return false;
    }
    let configured: URL;
    try {
        configured = new URL(rawBaseUrl);
    } catch {
        return false;
    }
    if (!isLoopbackHostname(configured.hostname)) {
        return false;
    }
    return !isLoopbackHostname(window.location.hostname);
}

export function getApiBaseUrl(): string {
    const raw = configuredApiUrl();
    if (!raw || shouldUseProxy(raw)) {
        return "";
    }
    return trimTrailingSlash(raw);
}

export function buildApiUrl(path: string): string {
    return `${getApiBaseUrl()}${path}`;
}
